import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { EASE, Label } from "./ui-bits";
import { AdamShareBar } from "@/pages/AdamSharePage";

export const ADAM_QUESTIONS = [
  "Do you have a decrease in libido (sex drive)?",
  "Do you have a lack of energy?",
  "Do you have a decrease in strength and/or endurance?",
  "Have you lost height?",
  "Have you noticed a decreased enjoyment of life?",
  "Are you sad and/or grumpy?",
  "Are your erections less strong?",
  "Have you noticed a recent deterioration in your ability to play sports?",
  "Are you falling asleep after dinner?",
  "Has there been a recent deterioration in your work performance?",
];

function getSessionId() {
  const key = "ah_session";
  let id = localStorage.getItem(key);
  if (!id) {
    id = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

export default function AdamQuestionnaire() {
  const [answers, setAnswers] = useState(Array(10).fill(null));
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState("idle");

  const answered = answers.filter((a) => a !== null).length;
  const done = answered === 10;

  const choose = (value) => {
    const next = [...answers];
    next[step] = value;
    setAnswers(next);
    if (step < 9) setStep(step + 1);
  };

  const submit = async () => {
    if (!done || status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/adam`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, session_id: getSessionId() }),
      });
      if (!res.ok) throw new Error("failed");
      setResult(await res.json());
      setStatus("done");
    } catch {
      // Score locally so the user still gets an answer if the network fails.
      const others = answers.filter((a, i) => a && i !== 0 && i !== 6).length;
      setResult({ positive: answers[0] || answers[6] || others >= 3, yes_count: answers.filter(Boolean).length });
      setStatus("done");
    }
  };

  const reset = () => {
    setAnswers(Array(10).fill(null));
    setStep(0);
    setResult(null);
    setStatus("idle");
  };

  if (result) return <AdamResult result={result} onReset={reset} />;

  return (
    <div className="card-line rounded-2xl p-6 sm:p-8" data-testid="adam-questionnaire">
      <div className="flex items-center justify-between gap-4">
        <Label>ADAM questionnaire</Label>
        <p className="mono-label text-dusk" data-testid="adam-progress">
          {String(step + 1).padStart(2, "0")} / 10
        </p>
      </div>

      <div className="h-px bg-line mt-5 relative overflow-hidden">
        <motion.div
          className="absolute left-0 top-0 h-full bg-teal"
          animate={{ width: `${(answered / 10) * 100}%` }}
          transition={{ duration: 0.5, ease: EASE }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="mt-8 min-h-[120px]"
        >
          <p className="font-display text-2xl sm:text-3xl tracking-wide leading-tight" data-testid="adam-question">
            {ADAM_QUESTIONS[step]}
          </p>
        </motion.div>
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3 mt-6">
        {[
          { v: true, label: "Yes" },
          { v: false, label: "No" },
        ].map((o) => (
          <button
            key={o.label}
            type="button"
            onClick={() => choose(o.v)}
            className={`rounded-xl border py-4 font-display text-xl tracking-wide transition-colors ${
              answers[step] === o.v ? "border-teal bg-teal/10 text-teal" : "border-line hover:border-teal/40 text-bone"
            }`}
            data-testid={`adam-answer-${o.label.toLowerCase()}`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        {ADAM_QUESTIONS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setStep(i)}
            aria-label={`Question ${i + 1}`}
            className={`w-8 h-8 rounded-full text-xs font-mono border transition-colors ${
              i === step ? "border-teal text-teal" : answers[i] !== null ? "border-line bg-surface text-bone/70" : "border-line text-dusk"
            }`}
            data-testid={`adam-step-${i + 1}`}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8">
        <p className="text-xs text-dusk leading-relaxed max-w-sm">
          Morley et al., 2000. A screening tool only — a positive result suggests a blood test, not a
          diagnosis. Your answers are stored anonymously.
        </p>
        <button
          type="button"
          onClick={submit}
          disabled={!done || status === "sending"}
          className="btn-primary justify-center disabled:opacity-40"
          data-testid="adam-submit"
        >
          {status === "sending" ? "Scoring…" : "See my result"}
        </button>
      </div>
    </div>
  );
}

function AdamResult({ result, onReset }) {
  return (
    <div className="card-line rounded-2xl p-6 sm:p-8 border-l-2 !border-l-teal" data-testid="adam-result">
      <Label>Your ADAM result</Label>
      <h3 className="font-display text-3xl sm:text-4xl tracking-wide mt-4" data-testid="adam-result-heading">
        {result.positive ? "Positive screen — a blood test is sensible." : "Negative screen — but symptoms still count."}
      </h3>
      <p className="text-mute text-sm sm:text-base leading-relaxed mt-4">
        You answered yes to {result.yes_count} of 10 questions.{" "}
        {result.positive
          ? "A positive ADAM screen means the next sensible step is a properly timed morning testosterone blood test, repeated if low. It is not a diagnosis: many men who screen positive have normal testosterone and symptoms with another cause."
          : "The ADAM questionnaire misses some men. If your symptoms are persistent and affecting your life, you still deserve a proper assessment — and the symptoms deserve attention whatever is causing them."}
      </p>
      <div className="flex flex-wrap gap-3 mt-7">
        <Link to="/book" className="btn-primary" data-testid="adam-result-book">
          Book an assessment
        </Link>
        <Link to="/testosterone-testing" className="btn-ghost" data-testid="adam-result-testing">
          How testing works
        </Link>
        <button type="button" onClick={onReset} className="text-sm text-mute hover:text-teal transition-colors px-2" data-testid="adam-reset">
          Retake
        </button>
      </div>
      <AdamShareBar resultId={result.id} />
      <p className="text-xs text-dusk leading-relaxed mt-6">
        Treatment is subject to individual clinical assessment. TRT is not suitable for everyone.
        For chest pain, stroke symptoms or thoughts of self-harm, seek urgent help (999 / 111).
      </p>
    </div>
  );
}
